import { type RefObject, useEffect } from 'react';

import { setIsPaused, setIsPlaying } from '../state/AudioPlayerState';

type UseAudioPlaybackSyncProps = {
	audioRef: RefObject<HTMLAudioElement>;
	onBuffer?: () => void;
};

export const useAudioPlaybackSync = ({ audioRef, onBuffer }: UseAudioPlaybackSyncProps): void => {
	useEffect(() => {
		const audio = audioRef.current;
		if (!audio) {
			return;
		}

		const onPlay = () => {
			setIsPlaying(true);
			setIsPaused(false);
		};

		const onPause = () => {
			setIsPlaying(false);
			if (audio.src && audio.src !== '') {
				setIsPaused(true);
			}
		};

		const onEnded = () => {
			setIsPlaying(false);
			setIsPaused(false);
		};

		const onCanPlay = () => {
			if (audio.currentTime < audio.duration) {
				audio.play();
			}
		};

		const handleBuffer = () => {
			onBuffer?.();
		};

		// Attach event listeners
		audio.addEventListener('play', onPlay);
		audio.addEventListener('pause', onPause);
		audio.addEventListener('canplay', onCanPlay);
		audio.addEventListener('ended', onEnded);
		audio.addEventListener('waiting', handleBuffer);

		return () => {
			// Cleanup event listeners
			audio.removeEventListener('play', onPlay);
			audio.removeEventListener('pause', onPause);
			audio.removeEventListener('canplay', onCanPlay);
			audio.removeEventListener('ended', onEnded);
			audio.removeEventListener('waiting', handleBuffer);
		};
	}, [audioRef, onBuffer]);
};
